'use client';

import { useMemo } from 'react';

import { NormalizedProduct } from '@ravago/shared/page-data/models/contentful-mapped';
import { TableCell } from '@ravago/shared/radiance/components/table/components/TableCell/TableCell';
import { TableHeader } from '@ravago/shared/radiance/components/table/components/TableHeader/TableHeader';
import { TableRow } from '@ravago/shared/radiance/components/table/components/TableRow/TableRow';
import { TableSubheader } from '@ravago/shared/radiance/components/table/components/TableSubheader/TableSubheader';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

interface Props {
   products: NormalizedProduct[];
   totalColumns: number;
}

export const ProductCompareSpecifications = ({ products, totalColumns }: Props) => {
   const translate = useTranslations();

   const specificationTypes = useMemo(() => {
      const types = new Set<string>();
      products.forEach((product) => {
         product.specifications?.forEach((spec) => {
            if (spec.provider?.name) {
               types.add(spec.provider.name);
            }
         });
      });
      return Array.from(types);
   }, [products]);

   return (
      <>
         <TableRow>
            <TableSubheader colSpan={totalColumns}>{translate('page-consumer.product-compare.table.specifications')}</TableSubheader>
         </TableRow>
         {specificationTypes.length > 0 ? (
            specificationTypes.map((type) => (
               <TableRow key={type}>
                  <TableHeader>
                     <Typography variant="body-highlight">{type ?? '-'}</Typography>
                  </TableHeader>
                  {products.map((product) => (
                     <TableCell verticalAlign="top" key={`specifications-${type}-${product.id}`}>
                        {product.specifications?.find((spec) => spec.provider?.name === type)?.name
                           ? product.specifications
                                .filter((spec) => spec.provider?.name === type)
                                .map((spec) => spec.name)
                                .join(' • ')
                           : '-'}
                     </TableCell>
                  ))}
               </TableRow>
            ))
         ) : (
            <TableRow>
               <TableCell colSpan={totalColumns}>
                  <Typography variant="body">{translate('page-consumer.product-compare.table.products-specifications-empty')}</Typography>
               </TableCell>
            </TableRow>
         )}
      </>
   );
};
